import { useCallback, useEffect, useRef, useState } from "react";
import { Brain } from "lucide-react";
import { useTranslation } from "react-i18next";
import { errorMessage, request } from "../../lib/api";
import { sendWrite } from "../../offline/writeQueue";
import { track } from "../../lib/events";
import { Status } from "../ui";
import { Question } from "./Question";
import type {
  AnswerMap,
  AnswerValue,
  ContentItem,
  LatestQuizAttempt,
  QuizAttempt,
  QuizQuestion,
  QuizResult,
} from "../../types";

// What submitting led to: "review" keeps the player on the quiz so the
// teacher can read the marked answers before moving on.
export type QuizSubmitOutcome = "failed" | "advance" | "review";

// What the module player needs to drive the quiz from its own Confirm & Next
// button: whether every question is answered, whether the quiz is already
// submitted (locked), and a way to submit it.
export interface QuizControl {
  ready: boolean;
  submitted: boolean;
  busy: boolean;
  submit: () => Promise<QuizSubmitOutcome>;
}

function isAnswered(question: QuizQuestion, answers: AnswerMap): boolean {
  if (question.type === "ordering") return true; // the shown order is an answer
  if (question.type === "section") return true; // a heading has nothing to answer
  if (question.type === "scenario") {
    return (question.children || []).every((child) => isAnswered(child, answers));
  }
  const value = answers[question.id];
  if (value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function QuestionNumber({ index, total }: { index: number; total: number }) {
  const { t } = useTranslation();
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wider text-accent">
      {t("questionNumber", { number: index + 1, total })}
    </span>
  );
}

export function Quiz({
  item,
  moduleTitle,
  onComplete,
  onControl,
}: {
  item: ContentItem;
  moduleTitle?: string;
  onComplete?: () => void;
  onControl?: (control: QuizControl | null) => void;
}) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  // Submitted while offline: the score is only known once the queue syncs.
  const [submittedOffline, setSubmittedOffline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The attempt is created on the first answer, not on opening the screen:
  // merely viewing one must not create an empty attempt.
  const attemptRef = useRef<Promise<QuizAttempt> | null>(null);
  const questions = (item.content.questions as QuizQuestion[] | undefined) || [];
  // Reopening a quiz shows the last attempt: answers to carry on with, or the
  // submitted answers (marked when the quiz shows feedback).
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    request<{ attempt: LatestQuizAttempt | null }>(
      `/api/teacher/quizzes/${encodeURIComponent(item.id)}/attempts/latest`
    )
      .then(({ attempt }) => {
        if (cancelled || !attempt) return;
        setAnswers(attempt.answers || {});
        if (attempt.status === "in_progress") {
          attemptRef.current = Promise.resolve({ id: attempt.id, attempt_no: attempt.attempt_no });
        } else {
          setResult({ score: attempt.score ?? 0, feedback: attempt.feedback });
        }
      })
      // Offline or unreachable: start fresh, as before.
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  function ensureAttempt(): Promise<QuizAttempt> {
    if (!attemptRef.current) {
      attemptRef.current = (async () => {
        const localRef = `local:${crypto.randomUUID()}`;
        const created = await sendWrite<QuizAttempt>({
          method: "POST",
          path: `/api/teacher/quizzes/${item.id}/attempts`,
          createsRef: localRef,
        });
        const value = created.queued ? { id: localRef, attempt_no: 0 } : created.data;
        track("quiz_started", { item_id: item.id, payload: { attempt_number: value.attempt_no } });
        return value;
      })();
      // A failed start must be retryable on the next answer.
      attemptRef.current.catch(() => {
        attemptRef.current = null;
      });
    }
    return attemptRef.current;
  }

  // Answers carried over from a submitted attempt, written into the new
  // attempt together with the first change so they are scored again.
  const carryOverRef = useRef<AnswerMap | null>(null);

  async function answer(question: QuizQuestion, value: AnswerValue) {
    if (result) {
      // Changing a submitted answer reopens the quiz as a new attempt.
      carryOverRef.current = { ...answers };
      attemptRef.current = null;
      setResult(null);
    }
    setAnswers((current) => ({ ...current, [question.id]: value }));
    setError("");
    // The phone's clock at the moment of choosing: the attempt may still have to
    // be created, and offline the upload happens much later.
    const chosenAt = new Date().toISOString();
    try {
      const attempt = await ensureAttempt();
      const carried = carryOverRef.current;
      carryOverRef.current = null;
      const writes = { ...(carried || {}), [question.id]: value };
      for (const [questionID, answerValue] of Object.entries(writes)) {
        if (answerValue === undefined) continue;
        await sendWrite({
          method: "PUT",
          path: `/api/teacher/attempts/${attempt.id}/responses/${questionID}`,
          body: {
            value: answerValue,
            // Every selection is kept server-side; the id makes a retried upload
            // count once. Carried-over answers are copies, not new choices.
            event_uuid: crypto.randomUUID(),
            answered_at: chosenAt,
            carried_over: carried !== null && questionID !== question.id,
          },
        });
      }
      track("answer_changed", { item_id: item.id, payload: { question: question.id } });
    } catch (cause) {
      // Refused by the server: say so on screen
      // rather than letting the rejection surface as an uncaught error.
      setError(errorMessage(cause));
    }
  }

  async function submit(): Promise<QuizSubmitOutcome> {
    setBusy(true);
    setError("");
    try {
      const attempt = await ensureAttempt();
      const submitted = await sendWrite<QuizResult>({
        method: "POST",
        path: `/api/teacher/attempts/${attempt.id}/submit`,
      });
      if (submitted.queued) {
        setSubmittedOffline(true);
        track("quiz_submitted", { item_id: item.id, payload: { offline: true } });
      } else {
        setResult(submitted.data);
        track("quiz_submitted", { item_id: item.id, payload: { score: submitted.data.score } });
        if (submitted.data.feedback?.length) {
          track("feedback_opened", { item_id: item.id, payload: { score: submitted.data.score } });
        }
      }
      onComplete?.();
      return !submitted.queued && submitted.data.feedback?.length ? "review" : "advance";
    } catch (cause) {
      setError(errorMessage(cause));
      return "failed";
    } finally {
      setBusy(false);
    }
  }

  const submitted = result !== null || submittedOffline;
  // A submitted quiz shows its answers (marked when feedback is on); changing one reopens it.
  const feedback = result
    ? Object.fromEntries((result.feedback || []).map((entry) => [entry.question_id, entry]))
    : undefined;
  const ready =
    questions.length > 0 && questions.every((question) => isAnswered(question, answers));
  // Section headings sit between questions but are not counted as one.
  let total = 0;
  const numbers = questions.map((question) => (question.type === "section" ? -1 : total++));

  // The control carries a stable submit() that always calls the latest closure,
  // so the player is only told about changes that matter, never every render.
  const submitRef = useRef(submit);
  useEffect(() => {
    submitRef.current = submit;
  });
  const stableSubmit = useCallback(() => submitRef.current(), []);
  useEffect(() => {
    onControl?.({ ready, submitted, busy, submit: stableSubmit });
  }, [onControl, ready, submitted, busy, stableSubmit]);
  useEffect(() => () => onControl?.(null), [onControl]);

  return (
    <section className="overflow-hidden rounded-xl bg-surface shadow-raised">
      <header className="flex items-center justify-between gap-2 bg-app-field px-4 py-2.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <Brain size={18} className="shrink-0 text-accent" />
          <span className="truncate text-xs font-semibold text-app-ink">
            {moduleTitle || item.title}
          </span>
        </span>
      </header>

      <div className="flex flex-col gap-6 p-4">
        {submittedOffline ? (
          <Status
            variant="success"
            title="Quiz saved"
            message="Your answers are saved on this device. They will be submitted and scored when you're back online."
          />
        ) : !loaded ? (
          <Status variant="loading" title={t("loading")} message="" />
        ) : result && feedback ? (
          <>
            {questions.map((question, index) => (
              <div key={question.id} className="flex flex-col gap-2">
                {question.type !== "section" && (
                  <QuestionNumber index={numbers[index]} total={total} />
                )}
                <Question
                  question={question}
                  value={answers[question.id]}
                  onChange={(value) => void answer(question, value)}
                  answers={answers}
                  onAnswerChild={(child, value) => void answer(child, value)}
                  feedback={feedback}
                />
              </div>
            ))}
          </>
        ) : (
          questions.map((question, index) => (
            <div key={question.id} className="flex flex-col gap-2">
              {question.type !== "section" && (
                <QuestionNumber index={numbers[index]} total={total} />
              )}
              <Question
                question={question}
                value={answers[question.id]}
                onChange={(value) => void answer(question, value)}
                answers={answers}
                onAnswerChild={(child, value) => void answer(child, value)}
              />
            </div>
          ))
        )}
        {error && (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-accent-strong" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
